import { FormikErrors } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { HeadingMedium, LockIcon } from '@arnold/common';
import { Origin, useResetPasswordMutation } from '../../generated/hooks';
import auth, { ErrorWithDetails } from '../../lib/auth';
import LoginForm from './LoginForm';
import OtpForm from './OtpForm';
import { GrantConsentForm } from './GrantConsentForm';
import { RoundLogo } from './Common';

interface IFormValues {
  email: string;
  password: string;
  responseError?: string;
  emailError?: string;
}

interface IProps {
  lang?: string;
}

interface ILocationState {
  otpEmail?: string;
  otpValidTo?: string;
  password?: string;
  username?: string;
}

enum ViewStates {
  LOGIN_WITH_PASSWORD,
  OTP,
  MISSING_CONSENT,
}

const LoginContainer = ({ lang }: IProps) => {
  const [t] = useTranslation('loginForm');
  const location = useLocation<ILocationState>();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect') ?? '';
  const [email, setEmail] = useState(
    searchParams.get('email') || location.state?.username || auth.getUserEmail() || '',
  );
  const [contactEmail, setContactEmail] = useState(location.state?.otpEmail || '');
  const [password, setPassword] = useState(location.state?.password || '');
  const [otpValidTo, setOtpValidTo] = useState(location.state?.otpValidTo || '');
  const [accessToken, setAccessToken] = useState('');
  // used for Talk to redirect to the page where the user was before login
  const redirectAfterLogin = redirect || '/overview';
  const [viewState, setViewState] = useState(
    location.state?.otpEmail && location.state?.otpValidTo ? ViewStates.OTP : ViewStates.LOGIN_WITH_PASSWORD,
  );
  const [resetPassword] = useResetPasswordMutation();

  const handleResetPassword = async (email: string) => {
    try {
      await resetPassword({ variables: { username: email, origin: Origin.SelfCare } });
    } catch (e) {
      console.error(e);
    }
    history.push(`/recoverPassword${lang ? `/${lang}` : ''}?email=${email}`);
  };

  const handleSignIn = async (
    email: string,
    password: string,
    otp: string | undefined,
    setErrors: (errors: FormikErrors<IFormValues>) => void,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    setEmail(email);
    setPassword(password);
    try {
      setSubmitting(true);
      await auth.login(email, password, otp, redirectAfterLogin);
    } catch (e) {
      setSubmitting(false);
      const response = (e as ErrorWithDetails).details;
      if ((e as ErrorWithDetails).status === 403) {
        if (response.otpValidTo && response.email) {
          setViewState(ViewStates.OTP);
          setContactEmail(response.email);
          setOtpValidTo(response.otpValidTo);
        } else if (response.hasOwnProperty('consent')) {
          setAccessToken(response.accessToken);
          setViewState(ViewStates.MISSING_CONSENT);
        }
      }
      if (response?.confirmedEmail === false) {
        setErrors({
          emailError: t('emailNotConfirmed'),
        });
      } else {
        setErrors({
          responseError: otp
            ? (e as ErrorWithDetails).status === 410
              ? t('expiredCode')
              : t('invalidCode')
            : (e as ErrorWithDetails).status === 401
              ? t('badCredentials')
              : t('somethingWentWrong'),
        });
      }
    }
  };

  return (
    <>
      <RoundLogo>
        <LockIcon />
      </RoundLogo>
      <HeadingMedium className="text-center">
        {t(
          viewState === ViewStates.MISSING_CONSENT
            ? 'grantConsent'
            : viewState === ViewStates.LOGIN_WITH_PASSWORD
              ? 'signInWithEmailTitle'
              : viewState === ViewStates.OTP
                ? 'signInWithOtpTitle'
                : 'magicLinkError',
        )}
      </HeadingMedium>
      {viewState === ViewStates.OTP && (
        <OtpForm
          email={email}
          password={password}
          otpValidTo={otpValidTo}
          handleSignIn={handleSignIn}
          contactEmail={contactEmail}
          lang={lang}
          goBack={() => setViewState(ViewStates.LOGIN_WITH_PASSWORD)}
        />
      )}
      {viewState === ViewStates.LOGIN_WITH_PASSWORD && (
        <LoginForm
          lang={lang}
          handleSignIn={handleSignIn}
          defaultEmail={email}
          redirect={redirect}
          handleResetPassword={handleResetPassword}
        />
      )}
      {viewState === ViewStates.MISSING_CONSENT && (
        <GrantConsentForm username={email} accessToken={accessToken} redirect={redirect} password={password} />
      )}
    </>
  );
};

export default LoginContainer;
